import { useMemo, useState } from 'react';
import { Stack, Title, Group, Button, TextInput, Table, Badge, Text, Anchor, ActionIcon, Tooltip, Skeleton, Alert } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSearch, IconTrash, IconAlertCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import fuzzysort from 'fuzzysort';
import { useTeam } from '../../hooks/useTeam';
import { useDeleteUser } from '../../hooks/useTeamMutations';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { CPUserViewData } from '@/lib/types/Auth';

export function TeamPage() {
  const navigate = useNavigate();
  const { users, isLoading, isError } = useTeam();
  const { authData } = useAuth();
  const deleteMutation = useDeleteUser();
  const [search, setSearch] = useState('');

  const currentIsAdmin = authData?.cpuser.core.isAdmin ?? false;

  const filteredUsers = useMemo(() => {
    if (!search.trim()) {
      return [...users].sort((a, b) => a.core.lastName.localeCompare(b.core.lastName));
    }
    const results = fuzzysort.go(search, users, {
      keys: ['core.firstName', 'core.lastName', 'core.email', 'core.username'],
      threshold: -1000,
    });
    return results.map(r => r.obj);
  }, [users, search]);

  function handleDelete(user: CPUserViewData) {
    modals.openConfirmModal({
      title: 'Delete Team Member',
      children: (
        <Text size="sm">
          Are you sure you want to delete <Text span fw={600}>{user.core.firstName} {user.core.lastName}</Text>?
          This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(user.core.cpuserId);
          notifications.show({ title: 'Deleted', message: 'Team member removed.', color: 'green' });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to delete user.',
            color: 'red',
          });
        }
      },
    });
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Team</Title>
        <Skeleton height={40} radius="md" />
        <Skeleton height={400} radius="md" />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack gap="lg">
        <Title order={2}>Team</Title>
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load team members. Please try again later.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Team</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate({ to: '/account/team/new' })}
        >
          Add User
        </Button>
      </Group>

      <TextInput
        placeholder="Search by name, email, or username..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        aria-label="Search team members"
      />

      {filteredUsers.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {search ? 'No users match your search.' : 'No team members yet.'}
        </Text>
      ) : (
        <Table highlightOnHover aria-label="Team members">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Username</Table.Th>
              <Table.Th>Roles</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th w={50} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredUsers.map(user => {
              const canDelete = !user.core.isAdmin
                && ((user.core.isAdmin && currentIsAdmin) || !user.core.isAdmin);

              return (
                <Table.Tr key={user.core.cpuserId}>
                  <Table.Td>
                    <Anchor
                      component="button"
                      size="sm"
                      fw={500}
                      ta="left"
                      onClick={() => navigate({ to: '/account/team/$id', params: { id: String(user.core.cpuserId) } })}
                    >
                      {user.core.firstName} {user.core.lastName}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{user.core.email}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">{user.core.username}</Text>
                  </Table.Td>
                  <Table.Td>
                    <PermissionBadges user={user} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {DateTime.fromSeconds(user.core.created).toLocaleString(DateTime.DATE_MED)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {canDelete && (
                      <Tooltip label="Delete">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDelete(user)}
                          aria-label={`Delete ${user.core.firstName} ${user.core.lastName}`}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function PermissionBadges({ user }: { user: CPUserViewData }) {
  const p = user.permissions;
  const badges: { label: string; color: string }[] = [];

  if (user.core.isAdmin) {
    badges.push({ label: 'Admin', color: 'violet' });
  }
  if (p.roleBroadcaster) {
    badges.push({ label: 'Broadcaster', color: 'orange' });
  }
  if (p.billingRead && p.billingWrite) {
    badges.push({ label: 'Billing', color: 'green' });
  }
  if (p.serviceRead && p.serviceWrite) {
    badges.push({ label: 'Content', color: 'blue' });
  }
  if (p.userRead && p.userWrite) {
    badges.push({ label: 'Users', color: 'cyan' });
  }
  if (p.subcustomerRead && p.subcustomerWrite) {
    badges.push({ label: 'Sub-accounts', color: 'teal' });
  }
  if (p.ingest) {
    badges.push({ label: 'Upload', color: 'indigo' });
  }

  if (badges.length === 0) {
    return <Text size="xs" c="dimmed">No permissions</Text>;
  }

  return (
    <Group gap={4}>
      {badges.map(b => (
        <Badge key={b.label} color={b.color} variant="light" size="xs">
          {b.label}
        </Badge>
      ))}
    </Group>
  );
}
